/**
 * Recital and Audition tracks (2026-09-27, build step 6 of "one home for
 * every student's tracks").
 *
 * Anthony, 2026-09-26: "At recital time, the student's track is usually
 * whatever cut is decided ... Occasionally, we'll make additional cuts if the
 * student needs to use it for an audition, but traditionally, when a song is
 * chosen for the recital, it stays that way unless it needs to be cut
 * somewhere down the line for time."
 *
 * So a ROLE marks one FILE for one student x song, on that student's
 * `repertoire` entry (the record Recital Planning already joins on by
 * canonical song id, and where the lesson's videos already live):
 *   recitalTrack         the ONE file the student performs to (a saved key
 *                        counts: it is its own file). Replacing it keeps the
 *                        previous in recitalTrackHistory, because a recital
 *                        cut changes only for a reason worth remembering.
 *   auditionTracks[]     any number of extra files.
 * Not a file-name convention: library files carry no student names on
 * purpose, so a name cannot say "final for this student" (decided 09-26).
 * Pure; pinned by trackRolesModel.test.js.
 */
import { playbackUrlFor, staffTrackLabel, tracksForSong } from './studentTracksModel';

/** What a role remembers about a file: enough to find it and to name it
 *  even if it is later removed from the library. */
export const fileRef = (file = {}) => ({
	storagePath: file.storagePath || null,
	variant: file.variant || null,
	length: file.length || null,
	key: file.key || null,
	name: file.name || null,
});

export const roleOf = (file, song) => {
	if (!file || !file.storagePath || !song) return null;
	if (song.recitalTrack && song.recitalTrack.storagePath === file.storagePath) return 'recital';
	if ((song.auditionTracks || []).some((a) => a && a.storagePath === file.storagePath)) return 'audition';
	return null;
};

/** The new auditionTracks array after turning one file on or off. */
export const toggledAuditions = (current = [], file, on, stamp = {}) => {
	const list = (Array.isArray(current) ? current : []).filter((a) => a && a.storagePath !== file.storagePath);
	return on ? [...list, { ...fileRef(file), ...stamp }] : list;
};

/** Any file with this Storage path across a student's pointer docs, even a
 *  superseded key: a chosen recital track stays findable after a new save. */
export const findFileByPath = (docs = [], storagePath) => {
	if (!storagePath) return null;
	for (const d of Array.isArray(docs) ? docs : []) {
		const f = (d.files || []).find((x) => x && x.storagePath === storagePath);
		if (f) return f;
	}
	return null;
};

/** A short label for a chosen track: "Track · Cut 60s · Down 2". */
export const refLabel = (ref = {}) => [staffTrackLabel(ref).split(' · ')[0], ref.length, ref.key].filter(Boolean).join(' · ');

/**
 * One performer's recital-track state for one set-list song:
 *   ready       a recital track is chosen and playable
 *   missing     one was chosen but its file is no longer on the student's record
 *   unchosen    tracks are on file for this song, none chosen yet
 *   none        no track on file for this song at all
 *   no-entry    the song is not in this student's repertoire (unlinked or absent)
 */
export const recitalStatus = ({ entry, trackDocs = [], song }) => {
	if (!entry) return { state: 'no-entry' };
	if (entry.recitalTrack && entry.recitalTrack.storagePath) {
		const file = findFileByPath(trackDocs, entry.recitalTrack.storagePath);
		if (file && playbackUrlFor(file)) return { state: 'ready', file, ref: entry.recitalTrack };
		return { state: 'missing', ref: entry.recitalTrack };
	}
	const onFile = tracksForSong(trackDocs, { canonicalSongId: song && song.canonicalSongId, name: entry.songName || (song && song.title) })
		.filter((f) => playbackUrlFor(f));
	return onFile.length ? { state: 'unchosen', count: onFile.length } : { state: 'none' };
};

export const RECITAL_STATE_TEXT = {
	ready: 'Recital track',
	missing: 'Recital track chosen, but the file is no longer on file',
	unchosen: 'No recital track chosen yet',
	none: 'No track on file for this song',
	'no-entry': 'Not in this student’s repertoire (song not linked)',
};
